import styles from '../TravelExpensesOverlayController.module.scss';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Button,
  Checkbox,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { cloneDeep, omit } from 'lodash';
import { DateTime } from 'luxon';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  BasicTableColumn,
  DataType,
  NumberCell,
  Table,
  useDialogs,
} from '@work4all/components';
import { EditableCell } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';
import { Card } from '@work4all/components/lib/dataDisplay/card';
import { useLock } from '@work4all/components/lib/hooks';

import { TravelAdditionalMealExpenses } from '@work4all/models/lib/Classes/TravelAdditionalMealExpenses.entity';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { TravelExpensesKind } from '@work4all/models/lib/Enums/TravelExpensesKind.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';
import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { DateTimeInputPicker } from '../../../../locked-inputs';
import { Collapse, ControlWrapper } from '../../../components';
import { useFormUpdate } from '../../../utils/use-form-update';
import { BusinessPartnerPickerCell } from '../../components/table-cells/BusinessPartnerPickerCell';
import { ProjectPickerCell } from '../../components/table-cells/ProjectPickerCell';
import { CurrencyExchangeInfoContext } from '../../inbound-invoice/currency-exchange-info-context';
import { TravelExpensesFormValue } from '../type';

import { AdditionalMealExpensesCardView } from './AdditionalMealExpensesCardView';
import { useCalculateTravelExpenses } from './use-calculate-travel-expanses';

type MealExpenseRow = TravelAdditionalMealExpenses & {
  projekt?: string;
  kundeLieferant?: string;
};

export const AdditionalMealExpenses = () => {
  const { t } = useTranslation();

  const dialogs = useDialogs();
  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const exchangeInfoContext = useContext(CurrencyExchangeInfoContext);

  const form = useFormContextPlus<TravelExpensesFormValue>();
  const { control, watch } = form;

  const { locked } = useLock();

  const watchAdditionalMealExpenses = watch('additionalMealExpenses');

  // Transform fields to match the display format
  const tableData = useMemo<MealExpenseRow[]>(() => {
    return (watchAdditionalMealExpenses ?? [])
      .map((field) => ({
        ...field,
        projekt: field.project?.name || '',
        kundeLieferant: '', // TODO: WW-7447 Map business partner data when available
      }))
      .sort(
        (a, b) =>
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );
  }, [watchAdditionalMealExpenses]);

  const calculateTravelExpenses = useCalculateTravelExpenses();

  const verify = useCallback(
    async (input?: TravelExpensesFormValue) => {
      const inputData = input || form.getValues();
      // take travelStart and travelEnd and filter out additionalMealExpenses that are not in that range
      const filteredAdditionalMealExpenses =
        inputData.additionalMealExpenses?.filter((x) => {
          // convert to ISO before you compare and compare with luxon DateTime
          // ignore time portion, use just date for comparison
          const expenseDate = DateTime.fromISO(x.dateTime).startOf('day');
          const travelStartDate = DateTime.fromISO(
            inputData.travelStart
          ).startOf('day');
          const travelEndDate = DateTime.fromISO(inputData.travelEnd).startOf(
            'day'
          );
          return expenseDate >= travelStartDate && expenseDate <= travelEndDate;
        });

      const returnData = await calculateTravelExpenses(
        omit(
          {
            ...inputData,
            additionalMealExpenses: filteredAdditionalMealExpenses,
          },
          '__typename'
        )
      );

      // Generate unique negative random IDs for items without an ID
      const generatedIds = new Set<number>();
      const generateUniqueNegativeId = (): number => {
        let id: number;
        do {
          id = -1 * (Math.random() * 1000000);
        } while (generatedIds.has(id));
        generatedIds.add(id);
        return parseInt(id.toString());
      };

      form.setValue(
        `additionalMealExpenses`,
        returnData.data.calculateTravelExpenses.additionalMealExpenses.map(
          (x) => ({
            ...x,
            id: x.code ? x.code : generateUniqueNegativeId(),
          })
        ),
        {
          shouldDirty: true,
        }
      );
    },
    [calculateTravelExpenses, form]
  );

  const addNewDay = useCallback(() => {
    const travelEnd = form.getValues('travelEnd');
    const newDate = DateTime.fromISO(travelEnd).plus({ days: 1 }).toISO();
    form.setValue('travelEnd', newDate);
    verify({ ...form.getValues(), travelEnd: newDate });
  }, [verify, form]);

  const createPositions = useCallback(() => {
    verify({ ...form.getValues() });
  }, [verify, form]);

  useFormUpdate(
    {
      country: (country) => {
        // Use ref to access the latest verify function to avoid stale closures
        verifyRef.current({ ...form.getValues(), country });
        return {};
      },
    },
    form
  );

  const update = useCallback(
    (
      id: number,
      key: string,
      value: number | string | boolean,
      updater?: (
        data: TravelAdditionalMealExpenses
      ) => TravelAdditionalMealExpenses
    ) => {
      const data = {
        ...cloneDeep(form.getValues()),
        additionalMealExpenses: watchAdditionalMealExpenses?.map((field) => {
          if (field.id === id) {
            if (updater) return updater(field);
            return {
              ...field,
              [key]: value,
            };
          }
          return field;
        }),
      };
      form.setValue(`additionalMealExpenses`, data.additionalMealExpenses, {
        shouldDirty: true,
      });
      return data;
    },
    [form, watchAdditionalMealExpenses]
  );

  const columns = useMemo<BasicTableColumn[]>(() => {
    const numberCell = (value: number, id: number, key: string) => (
      <EditableCell
        value={value}
        onExit={(e) => {
          const exitValue = e as number;
          const newValue = exitValue < 0 ? exitValue : -exitValue;
          update(id, key, newValue);
        }}
        type="number"
        isEditable
        isEditMode
        noBorder
        css={{
          color: 'var(--error)',
        }}
        cell={
          {
            column: {
              cellParams: {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            },
          } as any
        }
      />
    );

    return [
      {
        Header: t('COMMON.DATE'),
        accessor: 'dateTime',
        width: 100,
        Cell: (value) => {
          const date = value.row.original['dateTime'];
          return date
            ? DateTime.fromISO(date).toLocaleString(
                DateTimeCustom.DATE_SIMPLE_2YEAR
              )
            : '';
        },
      },
      {
        Header: 'h',
        accessor: 'hours',
        width: 50,
        dataType: DataType.Number,
        alignment: 'right',
      },
      {
        Header: t('TRAVEL.AMOUNT'),
        accessor: 'amount',
        width: 80,
        dataType: DataType.Currency,
        alignment: 'right',
        Cell: (props) => (
          <NumberCell
            {...props}
            minimumFractionDigits={2}
            maximumFractionDigits={2}
          />
        ),
      },
      {
        Header: t('TRAVEL.BREAKFAST_DEDUCTION'),
        accessor: 'deductionBreakfast',
        width: 120,
        Cell: (props) => {
          const isChecked = props.row.original.deductionBreakfast;
          const value = props.row.original.deductionBreakfastAmount;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                disabled={locked}
                checked={isChecked || false}
                sx={{ padding: 0 }}
                onChange={(e) => {
                  const data = update(
                    props.row.original.id,
                    'deductionBreakfast',
                    e.target.checked
                  );
                  verify(data);
                }}
              />
              {isChecked &&
                numberCell(
                  value,
                  props.row.original.id,
                  'deductionBreakfastAmount'
                )}
            </div>
          );
        },
      },
      {
        Header: t('TRAVEL.LUNCH_DEDUCTION'),
        accessor: 'deductionLunch',
        width: 120,
        Cell: (props) => {
          const isChecked = props.row.original.deductionLunch;
          const value = props.row.original.deductionLunchAmount;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                disabled={locked}
                checked={isChecked || false}
                sx={{ padding: 0 }}
                onChange={(e) => {
                  const data = update(
                    props.row.original.id,
                    'deductionLunch',
                    e.target.checked
                  );
                  verify(data);
                }}
              />
              {isChecked &&
                numberCell(
                  value,
                  props.row.original.id,
                  'deductionLunchAmount'
                )}
            </div>
          );
        },
      },
      {
        Header: t('TRAVEL.DINNER_DEDUCTION'),
        accessor: 'deductionDinner',
        width: 120,
        Cell: (props) => {
          const isChecked = props.row.original.deductionDinner;
          const value = props.row.original.deductionDinnerAmount;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                disabled={locked}
                checked={isChecked || false}
                sx={{ padding: 0 }}
                onChange={(e) => {
                  const data = update(
                    props.row.original.id,
                    'deductionDinner',
                    e.target.checked
                  );

                  verify(data);
                }}
              />
              {isChecked &&
                numberCell(
                  value,
                  props.row.original.id,
                  'deductionDinnerAmount'
                )}
            </div>
          );
        },
      },
      {
        Header: t('COMMON.PROJECT'),
        accessor: 'projekt',
        width: 250,
        Cell: (props) => {
          return (
            <ProjectPickerCell
              value={props.row.original.project}
              onChange={(project) => {
                const data = update(
                  props.row.original.id,
                  'projectId',
                  project?.id,
                  (data) => {
                    return {
                      ...data,
                      project: project,
                      projectId: project?.id,
                    };
                  }
                );
                verify(data);
              }}
            />
          );
        },
      },
      {
        Header: t('TRAVEL.CUSTOMER_SUPPLIER'),
        accessor: 'company',
        width: 200,
        Cell: (props) => {
          const data =
            props.row.original.company?.__typename === 'Geschaeftspartner'
              ? props.row.original.company?.data
              : props.row.original.company;
          return (
            <BusinessPartnerPickerCell
              value={data}
              onChange={(businessPartner) => {
                const data = update(
                  props.row.original.id,
                  'businessPartnerId',
                  businessPartner?.id,
                  (data) => {
                    return {
                      ...data,
                      businessPartnerId: businessPartner?.id,
                      businessPartnerType:
                        businessPartner?.__typename === 'Supplier'
                          ? SdObjType.LIEFERANT
                          : SdObjType.KUNDE,
                      company: businessPartner,
                    };
                  }
                );
                verify(data);
              }}
            />
          );
        },
      },
      {
        Header: t('INPUTS.NOTE/REMARK'),
        accessor: 'note',
        width: 250,
        Cell: (props) => (
          <EditableCell
            cell={props.cell}
            value={props.row.original.note || ''}
            onExit={(e) => {
              update(props.row.original.id, 'note', e as string);
            }}
            type="text"
            isEditable
            isEditMode
            noBorder
          />
        ),
      },
    ];
  }, [t, locked, verify, update]);

  const travelExpensesKindId = watch('travelExpensesKindId');

  const isDialogOpen = useRef<boolean>(false);
  const verifyRef = useRef(verify);
  const pendingTravelStartValue = useRef<string | null>(null);
  const pendingTravelEndValue = useRef<string | null>(null);

  // Update the ref whenever verify changes
  useEffect(() => {
    verifyRef.current = verify;
  }, [verify]);

  const handleTravelStartChange = useCallback(
    async (newValue: string, field: { onChange: (value: string) => void }) => {
      if (isDialogOpen.current) return;

      const currentValue = form.getValues('travelStart');

      // Compare as Date objects to handle different string formats
      const currentDate = currentValue ? new Date(currentValue) : null;
      const newDate = newValue ? new Date(newValue) : null;

      if (currentDate?.getTime() === newDate?.getTime()) {
        return;
      }

      const directSuccess =
        !watchAdditionalMealExpenses ||
        watchAdditionalMealExpenses.length === 0;

      if (directSuccess) {
        field.onChange(newDate?.toISOString() || '');
        await verifyRef.current({
          ...form.getValues(),
          travelStart: newDate?.toISOString() || '',
        });
        return;
      }

      isDialogOpen.current = true;
      const success = await dialogs.confirm({
        title: t('MASK.WARNING'),
        description: t('MASK.TRAVEL_EXPENSES_WARNING'),
      });
      isDialogOpen.current = false;
      if (success) {
        field.onChange(newDate?.toISOString() || '');
        await verifyRef.current({
          ...form.getValues(),
          travelStart: newDate?.toISOString() || '',
        });
      }
      // Clear the pending value after processing
      pendingTravelStartValue.current = null;
    },
    [form, watchAdditionalMealExpenses, dialogs, t]
  );

  const handleTravelEndChange = useCallback(
    async (newValue: string, field: { onChange: (value: string) => void }) => {
      if (isDialogOpen.current) return;

      const currentValue = form.getValues('travelEnd');

      // Compare as Date objects to handle different string formats
      const currentDate = currentValue ? new Date(currentValue) : null;
      const newDate = newValue ? new Date(newValue) : null;

      if (currentDate?.getTime() === newDate?.getTime()) {
        return;
      }

      const directSuccess =
        !watchAdditionalMealExpenses ||
        watchAdditionalMealExpenses.length === 0;

      if (directSuccess) {
        field.onChange(newDate?.toISOString() || '');
        await verifyRef.current({
          ...form.getValues(),
          travelEnd: newDate?.toISOString() || '',
        });
        return;
      }

      isDialogOpen.current = true;
      const success = await dialogs.confirm({
        title: t('MASK.WARNING'),
        description: t('MASK.TRAVEL_EXPENSES_WARNING'),
      });
      isDialogOpen.current = false;
      if (success) {
        field.onChange(newDate?.toISOString() || '');
        await verifyRef.current({
          ...form.getValues(),
          travelEnd: newDate?.toISOString() || '',
        });
      }
      // Clear the pending value after processing
      pendingTravelEndValue.current = null;
    },
    [form, watchAdditionalMealExpenses, dialogs, t]
  );

  const total = useMemo(() => {
    const _total = tableData.reduce((acc, curr) => {
      let rowTotal = curr.amount;
      // Add deduction amounts when checkboxes are checked
      if (curr.deductionBreakfast && curr.deductionBreakfastAmount) {
        rowTotal += curr.deductionBreakfastAmount;
      }
      if (curr.deductionLunch && curr.deductionLunchAmount) {
        rowTotal += curr.deductionLunchAmount;
      }
      if (curr.deductionDinner && curr.deductionDinnerAmount) {
        rowTotal += curr.deductionDinnerAmount;
      }
      return acc + rowTotal;
    }, 0);
    return Math.max(_total, 0);
  }, [tableData]);

  if (travelExpensesKindId !== TravelExpensesKind.STANDARD) return null;

  return (
    <Collapse title={t('MASK.ADDITIONAL_MEAL_EXPENSES')} defaultOpen>
      <Card>
        <ControlWrapper className={styles.additionalMealExpensesControls}>
          <div className={styles.timeRangeControls}>
            <ControllerPlus
              name="travelStart"
              control={control}
              render={({ field }) => {
                return (
                  <DateTimeInputPicker
                    dateLabel={t('INPUTS.START')}
                    clearable={false}
                    required
                    value={field.value}
                    onDestroyPopover={() => {
                      if (pendingTravelStartValue.current !== null) {
                        handleTravelStartChange(
                          pendingTravelStartValue.current,
                          field
                        );
                      }
                    }}
                    onChange={(
                      newValue: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      // Store the value in ref for later processing in onDestroyPopover
                      pendingTravelStartValue.current = newValue.target.value;
                    }}
                  />
                );
              }}
            />

            <div className={styles.timeRangeArrow}>
              <ArrowForwardIcon />
            </div>
            <ControllerPlus
              name="travelEnd"
              control={control}
              render={({ field }) => {
                return (
                  <DateTimeInputPicker
                    dateLabel={t('INPUTS.END')}
                    clearable={false}
                    required
                    value={field.value}
                    onDestroyPopover={() => {
                      if (pendingTravelEndValue.current !== null) {
                        handleTravelEndChange(
                          pendingTravelEndValue.current,
                          field
                        );
                      }
                    }}
                    onChange={(
                      newValue: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      // Store the value in ref for later processing in onDestroyPopover
                      pendingTravelEndValue.current = newValue.target.value;
                    }}
                  />
                );
              }}
            />
          </div>
        </ControlWrapper>

        <div style={{ marginTop: '16px' }}>
          {isViewportDownMd ? (
            <AdditionalMealExpensesCardView
              onVerify={verify}
              update={update}
              tableData={tableData}
            />
          ) : (
            <Table
              data={tableData}
              columns={columns}
              mode="client"
              selectableRows={false}
              fitContentHeight
              isVirtual={false}
              classes={{
                row: isViewportDownMd ? styles.row : undefined,
              }}
            />
          )}

          <div
            style={{
              marginTop: '12px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {!watchAdditionalMealExpenses ||
            watchAdditionalMealExpenses.length === 0 ? (
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={createPositions}
                color="primary"
                disabled={locked}
              >
                {t('TRAVEL.ADD_DAY')}
              </Button>
            ) : (
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={addNewDay}
                color="primary"
                disabled={locked}
              >
                {t('TRAVEL.ADD_DAY')}
              </Button>
            )}

            <Typography variant="body1" fontWeight={700}>
              {`${t('COMMON.TOTAL')}: ${formatNumberAsCurrency(total)}${
                exchangeInfoContext?.defaultCurrency
                  ? ` ${formatCurrency(exchangeInfoContext.defaultCurrency, t)}`
                  : ''
              }`}
            </Typography>
          </div>
        </div>
      </Card>
    </Collapse>
  );
};
